import { UseFormRegister } from "react-hook-form";

import { LinkageModalInput } from "app/(PROTECTED)/property/[propertyId]/page.tsx";
import { Checkbox } from "components";
import { DocumentField } from "constants/file";
import { Confirm, Error, Info, Notification } from "store/modalStore";
import { AcceptFileType, createSubMessage } from "utils/modalMessage.util";
/**
 * 公開URL(public domain)使用済み infoモーダル
 * */
export const PUBLIC_URL_IN_USE = (): Omit<Info, "modalState"> => ({
  infoMessage: "ご希望の公開URLは既に使われています",
  subMessage: (
    <p>
      他の公開URLを入力して
      <br />
      もう一度お試しください
    </p>
  ),
});

/**
 * 登録成功のinfoモーダル
 * */
export const CREATE_SUCCEED = (onClose: () => void): Omit<Info, "modalState"> => ({
  infoMessage: "登録しました",
  onClose: onClose,
});

/**
 * 更新成功のinfoモーダル
 * */
export const UPDATE_SUCCEED = (onClose: () => void): Omit<Info, "modalState"> => ({
  infoMessage: "変更しました",
  onClose: onClose,
});

/**
 * 送信成功のinfoモーダル
 * */
export const SEND_SUCCEED = (onClose: () => void): Omit<Info, "modalState"> => ({
  infoMessage: "送信しました",
  onClose: onClose,
});

/**
 * 削除成功のinfoモーダル
 * */
export const DELETE_SUCCEED = (onClose: () => void): Omit<Info, "modalState"> => ({
  infoMessage: "削除しました",
  onClose: onClose,
});

/**
 * パスワード変更成功 infoモーダル
 * @param onClose : () => void
 * */
export const PASSWORD_CHANGED = (onClose: () => void): Omit<Info, "modalState"> => ({
  infoMessage: "パスワードを変更しました",
  onClose: onClose,
});

/**
 * 申込内容確認完了のinfoモーダル
 * */
export const CONFIRMATION_SUCCEED = (onClose: () => void): Omit<Info, "modalState"> => ({
  infoMessage: "確認が完了しました",
  onClose: onClose,
});

/**
 * 申込内容変更確認完了のinfoモーダル
 * */
export const REQUEST_CHANGE_APPLICATION_SUCCEED = (onClose: () => void): Omit<Info, "modalState"> => ({
  infoMessage: "申込内容の変更を依頼しました",
  onClose: onClose,
});

/**
 * 手続き終了完了のinfoモーダル
 * */
export const CANCEL_APPLICATION_SUCCEED = (onClose: () => void): Omit<Info, "modalState"> => ({
  infoMessage: "終了にしました",
  onClose: onClose,
});

/**
 * 申込内容変更不可完了のinfoモーダル
 * */
export const LOCK_APPLICATION_SUCCEED = (onClose: () => void): Omit<Info, "modalState"> => ({
  infoMessage: "申込内容変更を不可にしました",
  onClose: onClose,
});

/**
 * 送信失敗(ファイルサイズ極大) errorモーダル
 * */
export const PAYLOAD_TOO_LARGE = (): Omit<Error, "modalState"> => ({
  buttonText: "閉じる",
  errorMessage: "アップロードに失敗しました",
  subMessage: "ファイルサイズが大きすぎます",
});
/**
 * 送信失敗 errorモーダル
 * */
export const EXCEPTION: Omit<Error, "modalState"> = {
  buttonText: "閉じる",
  errorMessage: "送信に失敗しました",
  subMessage: "時間をおいてもう一度お試しください",
};

/**
 * 受信失敗 errorモーダル
 * */
export const FETCH_FAILED = (onClose?: () => void): Omit<Error, "modalState"> => ({
  buttonText: "閉じる",
  errorMessage: "通信に失敗しました",
  subMessage: "時間をおいてもう一度お試しください",
  onClose: onClose,
});

/**
 * アップロード失敗 ファイル形式の制限 errorモーダル
 * */
export const INVALID_FILE_TYPE = (acceptFileTypes: AcceptFileType[]): Omit<Error, "modalState"> => ({
  errorMessage: "アップロードに失敗しました",
  subMessage: "アップロードできないファイル形式です",
  attentionMessage: `ファイル形式は${createSubMessage(acceptFileTypes)}のみ`,
  buttonText: "閉じる",
});

/**
 * 物件登録確認 confirmモーダル
 * */
export const CREATE_PROPERTY_CONFIRM = (
  subMessage: string,
  positiveAction?: () => void
): Omit<Confirm, "modalState"> => ({
  confirmMessage: "物件を登録しますか？",
  subMessage: subMessage,
  positiveButtonText: "登録する",
  positiveAction: positiveAction,
});

/**
 * 物件更新確認 confirmモーダル
 * */
export const UPDATE_PROPERTY_CONFIRM = (
  subMessage: string,
  positiveAction?: () => void
): Omit<Confirm, "modalState"> => ({
  confirmMessage: "物件情報を変更しますか？",
  subMessage: subMessage,
  positiveButtonText: "変更する",
  positiveAction: positiveAction,
});

/**
 * 物件更新確認 confirmモーダル
 * */
export const DELETE_PROPERTY_NOTIFICATION = (positiveAction: () => void): Omit<Notification, "modalState"> => ({
  notificationMessage: "削除しますか？",
  subMessage: (
    <>
      物件を削除すると
      <br />
      区画グループと区画も削除されます
    </>
  ),
  positiveButtonText: "削除する",
  positiveAction: positiveAction,
});

/**
 * 区画削除確認 confirmモーダル
 * */
export const DELETE_SPACE_NOTIFICATION = (positiveAction: () => void): Omit<Notification, "modalState"> => ({
  notificationMessage: "削除しますか？",
  positiveButtonText: "削除する",
  positiveAction: positiveAction,
});

/**
 * 連携を解除確認 confirmモーダル
 * */
export const UNLINKING_NOTIFICATION = (
  positiveAction: () => void,
  cancelAction: () => void
): Omit<Notification, "modalState"> => ({
  notificationMessage: "連携を解除します",
  isHiddenWarningIcon: true,
  positiveButtonText: "解除する",
  positiveAction: positiveAction,
  cancelAction: cancelAction,
});

/**
 * メッセージテンプレート削除確認 confirmモーダル
 * */
export const DELETE_MESSAGE_TEMPLATE_NOTIFICATION = (
  positiveAction: (_: string) => void,
  messageId: string
): Omit<Notification, "modalState"> => ({
  positiveButtonText: "削除する",
  notificationMessage: "削除しますか？",
  positiveAction: () => positiveAction(messageId),
});

/**
 * 区画グループ登録時のconfirmモーダル
 * */
export const CREATE_SPACE_GROUP_CONFIRM = (
  subMessage: string,
  positiveAction: () => void
): Omit<Confirm, "modalState"> => ({
  confirmMessage: "区画グループを登録しますか?",
  subMessage: subMessage,
  positiveButtonText: "登録する",
  positiveAction: positiveAction,
});

/**
 * 区画グループ編集時のconfirmモーダル
 * */
export const UPDATE_SPACE_GROUP_CONFIRM = (
  subMessage: string,
  positiveAction: () => void
): Omit<Confirm, "modalState"> => ({
  confirmMessage: "区画グループ情報を変更しますか?",
  subMessage: subMessage,
  positiveButtonText: "変更する",
  positiveAction: positiveAction,
});

/**
 * 区画グループ編集時のconfirmモーダル
 * */
export const CREATE_SPACE_CONFIRM = (subMessage: string, positiveAction: () => void): Omit<Confirm, "modalState"> => ({
  confirmMessage: "区画を登録しますか?",
  subMessage: subMessage,
  positiveButtonText: "登録する",
  positiveAction: positiveAction,
});

/**
 * 区画グループ編集時のconfirmモーダル
 * */
export const AT_PORT_LINKAGE_CONFIRM = (
  positiveAction: () => void,
  cancelAction: () => void,
  register: UseFormRegister<LinkageModalInput>
): Omit<Confirm, "modalState"> => ({
  confirmMessage: (
    <p className="text-center	">
      at PORTに連携して
      <br />
      集客を依頼します
    </p>
  ),
  subMessage: (
    <div className="mt-2">
      <Checkbox<LinkageModalInput> label="1週間以内での集客を希望" name="isHopeOneWeek" register={register} />
    </div>
  ),
  positiveButtonText: "連携する",
  positiveAction: positiveAction,
  cancelAction: cancelAction,
  top: "mt-10",
});

/**
 * 区画グループ編集時のconfirmモーダル
 * */
export const UPDATE_SPACE_CONFIRM = (subMessage: string, positiveAction: () => void): Omit<Confirm, "modalState"> => ({
  confirmMessage: "変更しますか?",
  subMessage: subMessage,
  positiveButtonText: "変更する",
  positiveAction: positiveAction,
});

/**
 * ページバック確認モーダル
 * */
export const INTERCEPT_TRANSITION = (
  positiveAction: () => void,
  cancelAction?: () => void
): Omit<Confirm, "modalState"> => ({
  confirmMessage: "このページから移動しますか？",
  subMessage: "入力中の情報は破棄されます",
  positiveButtonText: "ページから移動",
  positiveAction: positiveAction,
  cancelAction: cancelAction,
});

/**
 * 物件更新確認 confirmモーダル
 * */
export const DELETE_SPACE_GROUP_NOTIFICATION = (positiveAction: () => void): Omit<Notification, "modalState"> => ({
  notificationMessage: "削除しますか？",
  subMessage: (
    <>
      区画グループを削除すると
      <br />
      紐づく全ての区画が削除されます
    </>
  ),
  positiveButtonText: "削除する",
  positiveAction: positiveAction,
});

/**
 * 申込確認 confirmモーダル
 * */
export const CONFIRM_APPLICATION = (positiveAction: () => void): Omit<Confirm, "modalState"> => ({
  confirmMessage: "確認を完了にしますか？",
  positiveButtonText: "完了にする",
  positiveAction: positiveAction,
});

/**
 * 契約書類削除確認モーダル
 * */
export const DELETE_DOCUMENT_CONFIRM = (
  positiveAction: (field: DocumentField) => void
): Omit<Notification, "modalState"> => ({
  notificationMessage: "削除しますか？",
  subMessage: "この操作は元に戻せません",
  positiveButtonText: "削除する",
  positiveAction: positiveAction,
});

export const DELETE_COMMON = (positiveAction: () => void): Omit<Notification, "modalState"> => ({
  notificationMessage: "削除しますか？",
  positiveButtonText: "削除する",
  positiveAction: positiveAction,
});

/**
 * 申込確認 利用期間重複 errorモーダル
 * */
export const DUPLICATION_SCHEDULE: Omit<Info, "modalState"> = {
  infoMessage: "利用期間が重複しています",
  subMessage: "区画と利用期間をご確認ください",
};

/**
 * 申込確認 区画満車 errorモーダル
 * */
export const FILL_SPACE: Omit<Info, "modalState"> = {
  infoMessage: "利用区画は満車です",
  subMessage: "区画と期間をご確認ください",
};

/**
 * モーダルタイトルのID名のデフォルト
 * */
export const DEFAULT_MODAL_TITLE_ID = "modal-title";

/**
 * モーダルボディのID名のデフォルト
 * */
export const DEFAULT_MODAL_BODY_ID = "modal-body";

export const CUSTOMER_CANCEL_CONFIRM = (positiveAction?: () => void): Omit<Confirm, "modalState"> => ({
  confirmMessage: "このページから移動しますか？",
  subMessage: "入力中の情報は破棄されます",
  positiveButtonText: "ページから移動",
  positiveAction: positiveAction,
});

/**
 * メール通知グループ削除確認モーダル
 */
export const DELETE_MAILING_GROUP_NOTIFICATION = (positiveAction: () => void): Omit<Notification, "modalState"> => ({
  notificationMessage: "削除しますか？",
  positiveButtonText: "削除する",
  positiveAction: positiveAction,
});

/**
 * メール通知グループ登録・更新確認モーダル
 */
export const SAVE_MAILING_GROUP_CONFIRM = (positiveAction: () => void): Omit<Confirm, "modalState"> => ({
  confirmMessage: (
    <p className="text-center">
      メール通知グループを
      <br />
      登録しますか？
    </p>
  ),
  positiveButtonText: "登録する",
  positiveAction: positiveAction,
});
